import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import { getCompanyById, getCompanyContacts, linkCompanyAccount, unlinkCompanyAccount } from '../api/crmApi';
import { crmCompanyKeys } from './useCrmCompanies';

export const companyDetailKeys = {
  all: ['crm-company-detail'] as const,
  detail: (id: string) => [...companyDetailKeys.all, id] as const,
  contacts: (id: string) => [...companyDetailKeys.all, id, 'contacts'] as const,
};

export function useCompanyDetail(id: string) {
  return useQuery({
    queryKey: companyDetailKeys.detail(id),
    queryFn: () => getCompanyById(id),
    staleTime: 2 * 60 * 1000,
    enabled: !!id,
  });
}

export function useCompanyContacts(companyId: string) {
  return useQuery({
    queryKey: companyDetailKeys.contacts(companyId),
    queryFn: () => getCompanyContacts(companyId),
    staleTime: 2 * 60 * 1000,
    enabled: !!companyId,
  });
}

export function useLinkAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, accountId }: { companyId: string; accountId: string }) =>
      linkCompanyAccount(companyId, accountId),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyDetailKeys.detail(companyId) });
      queryClient.invalidateQueries({ queryKey: crmCompanyKeys.all });
      toast.success(i18next.t('crm:companies.toast.linked'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:companies.toast.failedLink'), { description: error.message });
    },
  });
}

export function useUnlinkAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => unlinkCompanyAccount(companyId),
    onSuccess: (_, companyId) => {
      queryClient.invalidateQueries({ queryKey: companyDetailKeys.detail(companyId) });
      queryClient.invalidateQueries({ queryKey: crmCompanyKeys.all });
      toast.success(i18next.t('crm:companies.toast.unlinked'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:companies.toast.failedUnlink'), { description: error.message });
    },
  });
}
